/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getNotice } from '@/lib/api'

/**
 * 系统公告顶部横条(apiyi 风格)。
 *
 * 营销主页不再弹公告对话框,而是把系统设置里的「公告 Notice」渲染成菜单栏
 * 上方的一条蓝色渐变横条(参考 api.apiyi.com 顶部促销条,同款
 * from-blue-600 → to-indigo-600 底色)。内容取公告首行并剥离
 * markdown/HTML 标记压成单行,保证横条高度恒定(h-10),便于 PublicHeader
 * 把自己下移固定的 40px。公告为空时整条不渲染,页头回到 top-0。
 */

/** Strip markdown/HTML down to a single plain-text line for the banner. */
function noticeToPlainLine(raw: string): string {
  let s = raw
  s = s.replace(/!\[[^\]]*\]\([^)]*\)/g, '') // images
  s = s.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // links → keep label
  s = s.replace(/<[^>]+>/g, ' ') // html tags
  s = s.replace(/^#{1,6}\s+/gm, '') // heading marks
  s = s.replace(/[*_`~]{1,3}/g, '') // emphasis / code marks
  s = s.replace(/\s+/g, ' ').trim()
  return s
}

/**
 * Fetch the system notice (same query key/cache as use-notifications) and
 * return it flattened to one plain-text line; '' when absent or disabled.
 */
export function useAnnouncementBarNotice(enabled: boolean): string {
  const { i18n } = useTranslation()
  const noticeLang = (i18n.language || '').split('-')[0] || 'zh'
  const { data } = useQuery({
    queryKey: ['notice', noticeLang],
    queryFn: () => getNotice(noticeLang),
    staleTime: 1000 * 60 * 5,
    enabled,
  })
  if (!enabled) return ''
  const raw = data?.success ? String(data.data || '').trim() : ''
  return raw ? noticeToPlainLine(raw) : ''
}

export function AnnouncementBar(props: { text: string }) {
  const { t } = useTranslation()
  return (
    <div
      role='status'
      className='fixed inset-x-0 top-0 z-50 flex h-10 items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-indigo-600 px-4 text-white'
    >
      <span
        className='truncate text-[13px] font-medium'
        title={props.text}
      >
        {props.text}
      </span>
      {/* 公告后置「免费试用」白底小按钮 → 注册页 */}
      <a
        href='/sign-up'
        className='shrink-0 rounded-full bg-white px-3 py-1 text-xs font-semibold text-blue-600 shadow-sm transition-transform hover:scale-[1.03] hover:bg-blue-50'
      >
        {t('Free Trial')}
      </a>
    </div>
  )
}
